import React, { EffectCallback } from 'react';
import { connect } from 'react-redux';
import Button from '@material-ui/core/Button';
import TextField from '@material-ui/core/TextField';
import Icon from '@material-ui/core/Icon';
import FormControl from '@material-ui/core/FormControl';
import Input from '@material-ui/core/Input';
import EuroIcon from '@material-ui/icons/Euro';
import CheckIcon from '@material-ui/icons/Check';
import { withTranslation, WithTranslation } from 'react-i18next';
import { Link as RouterLink, RouteComponentProps } from 'react-router-dom';
// Components
import App from '../../../components/app';
import ErrorMessage from '../../../components/error-message';
import { Dialog } from '../../../components/dialog';
import EnhancedTable from '../../../components/table';
import { TooltipButton } from '../../../components/tooltip-button';
import MainLoading from '../../../components/main-loading';
import { ActionCustom, FormatType, HeadCell, PickerOption } from '../../../local-types';
// Modules
import { actions as billActions } from '../../../redux/reducers/bills';
import { ErrorPayload, selectors as errorSelectors } from '../../../redux/reducers/errors';
import { selectors as pendingSelectors } from '../../../redux/reducers/pendingOperations';
import { BillPaymentType, BillStatus, CreditNoteType } from '../../../redux/reducers/api-types';
import { tableRequestPoints } from '../../../redux/reducers/constants';
// ressources
import DateUtils from '../../../utils/dateUtils';
import FormattingUtils from '../../../utils/formattingUtils';
import withGlobalStyles from '../../../utils/global-styles';
import UserUtils, { AccountRight, RightRank } from '../../../utils/userUtils';
import { paths } from '../../paths';
import { Cell, createCell } from '../../../components/table/table-cell-creator';

const queryString = require('query-string');

interface GroupedBillProps extends WithTranslation, RouteComponentProps {
    globalClasses: any;
    loadingRetryPaiement: any;
    groupedBillsActionsError: ErrorPayload;
    setPaiement: (id: string, flag: string, amount: number, callback?: EffectCallback) => any;
    exportSepa: (payload: { start_date: number; end_date: number }, callback?: EffectCallback) => any;
    exportPaiement: (payload: { start_date: number; end_date: number }, callback?: EffectCallback) => any;
    retryPaiement: (id: string, callback?: EffectCallback) => any;
}

class GroupedBillListPage extends React.Component<GroupedBillProps> {
    isAdmin: boolean;
    tableRef: any;
    filterParams: any;
    statusOptions: PickerOption[];
    paiementOptions: PickerOption[];

    canWriteCreditNote: boolean;
    canRectify: boolean;
    canExport: boolean;
    canRetryPayment: boolean;

    constructor(props: GroupedBillProps) {
        super(props);
        this.filterParams = queryString.parse(this.props.location.search);
        if (!this.filterParams?.['after[created_at]']) {
            const twoMonthsAgo = new Date();
            twoMonthsAgo.setMonth(twoMonthsAgo.getMonth() - 2);
            this.filterParams = {
                ...this.filterParams,
                'after[created_at]': DateUtils.dateFormat(twoMonthsAgo, 'yyyy-mm-dd'),
            };
        }

        this.isAdmin = UserUtils.checkRightsOrAdmin({
            name: AccountRight.RIDER_BILLING_MGMT,
            mode: RightRank.W,
        });
        this.canWriteCreditNote = UserUtils.checkRightsOrAdmin({
            name: AccountRight.CREDIT_NOTE_MGMT,
            mode: RightRank.W,
        });
        this.canExport = UserUtils.checkRightsOrAdmin({
            name: AccountRight.RIDER_BILLING_EXPORT,
            mode: RightRank.R,
        });
        this.canRectify = UserUtils.checkRightsOrAdmin({ name: AccountRight.BILL_RECTIFICATION, mode: RightRank.W });
        this.canRetryPayment = UserUtils.checkRightsOrAdmin({
            name: AccountRight.RIDER_BILLING_PYMT_RETRY,
            mode: RightRank.W,
        });

        const { t } = props;
        this.statusOptions = [
            { value: '', label: '' },
            { value: BillStatus.PAID, label: t('statusOptions.paid') },
            { value: BillStatus.PAYMENT_MANUAL, label: t('statusOptions.not_paid') },
            // { value: BillStatus.PENDING, label: t('statusOptions.pending') },
            { value: BillStatus.CANCELLED, label: t('statusOptions.cancelled') },
        ];
        this.paiementOptions = [
            { value: '', label: '' },
            { value: BillPaymentType.TRANSFER, label: t('common:paiement.wire_transfer') },
            { value: BillPaymentType.SEPA, label: t('common:paiement.sepa') },
            { value: BillPaymentType.ADYEN, label: t('common:paiement.card_adyen') },
        ];
    }

    handleAction = (row, fields): void => {
        const { price } = fields;
        const { id, amount_with_tax } = row;
        const remaining = Math.max(amount_with_tax - price, 0);
        const flag = price >= amount_with_tax ? BillStatus.PAID : BillStatus.PAYMENT_MANUAL;
        this.props.setPaiement(id, flag, remaining, () => {
            this.tableRef.reload();
        });
    };

    handleRetryPaiement = (id) => (e) => {
        this.props.retryPaiement(id, () => {
            this.tableRef.reload();
        });
    };

    renderRetryPaiementLoader = (row, id): JSX.Element => {
        const activeRow = this.props.loadingRetryPaiement?.id === row.id;

        if (activeRow && id) {
            return <MainLoading />;
        }
        return null;
    };

    headerBuilder = (): HeadCell[] => {
        const { t, globalClasses } = this.props;
        const headers: HeadCell[] = [
            createCell(t, Cell.BILL_NUMBER),
            createCell(t, Cell.BILL_DOCUMENT_PATH),
            createCell(t, Cell.BILL_XLS_PATH),
            createCell(t, Cell.BILL_CREATED_AT),
            {
                id: 'start_date',
                label: t('facturation_period'),
                filterable: false,
                formatCell: function (row) {
                    return `${DateUtils.tsToDate(row.start_date, true)} - ${DateUtils.tsToDate(row.end_date, true)}`;
                },
            },
            createCell(t, Cell.COMPANY_ID),
            {
                id: 'status',
                label: t('common:table.status'),
                type: FormatType.Picker,
                sortable: false,
                pickerOptions: this.statusOptions,
                customValue: function (value) {
                    if (value === BillStatus.PAYMENT_MANUAL) {
                        return {
                            'in[status]': [
                                'pending',
                                'payment_pending',
                                'payment_fail_soft',
                                'payment_fail_hard',
                                'payment_manual',
                            ],
                        };
                    }
                    return null;
                },
                formatCell: function (row) {
                    let res = t('statusOptions.paid');
                    if (row.status === BillStatus.CANCELLED) {
                        res = t('statusOptions.cancelled');
                    } else if (row.status !== BillStatus.PAID) {
                        res = t('statusOptions.not_paid');
                    }
                    return res;
                },
            },
            {
                id: 'remaining_balance',
                label: t('remaining_balance'),
                type: FormatType.Price,
                sortable: false,
                filterable: false,
                formatCell: function (row) {
                    let res = FormattingUtils.price(0, true);
                    if (row.status !== BillStatus.PAID) {
                        const remaining =
                            row.payment_type !== BillPaymentType.ADYEN ? row.remaining_balance : row.amount_with_tax;
                        res = FormattingUtils.price(remaining);
                    }
                    return res;
                },
            },
            // createCell(t, Cell.AMOUNT_PRE_TAX),
            createCell(t, Cell.AMOUNT_WITH_TAX),
            {
                id: 'payment_type',
                label: t('payment_type'),
                sortable: false,
                type: FormatType.Picker,
                pickerOptions: this.paiementOptions,
                formatCell: function (row) {
                    return t('common:paiement.' + row.payment_type);
                },
            },
        ];

        if (this.isAdmin || this.canRetryPayment || this.canRectify || this.canWriteCreditNote) {
            headers.push({
                id: 'credit',
                label: t('common:action'),
                filterable: false,
                loaderCell: (row, id) => this.renderRetryPaiementLoader(row, id),
                formatCell: (row) => {
                    let res = null;
                    if (row.status !== BillStatus.PAID && row.payment_type !== BillPaymentType.ADYEN) {
                        res = this.isAdmin && (
                            <>
                                <Dialog
                                    btnTitle={t('addPaidAmount')}
                                    description={t('paid_description', { amount: row.amount_with_tax })}
                                    customButton={
                                        <TooltipButton title={t('addPaidAmount')}>
                                            <Button
                                                size="small"
                                                variant="contained"
                                                color="primary"
                                                aria-label="action"
                                                style={{ marginRight: 8 }}
                                                className={globalClasses.btnAutoWidth}
                                            >
                                                <EuroIcon fontSize="small" />
                                            </Button>
                                        </TooltipButton>
                                    }
                                    formComponent={
                                        <TextField
                                            autoFocus
                                            margin="dense"
                                            id="price"
                                            label={t('paid_placeholder')}
                                            type="number"
                                            name="price"
                                            required={true}
                                            fullWidth
                                            inputProps={{ step: 0.01, min: 0 }}
                                        />
                                    }
                                    onOK={(fields) => this.handleAction(row, fields)}
                                />
                                <Dialog
                                    btnTitle={t('validate_payment')}
                                    description={t('validate_payment_msg', {
                                        id: row.bill_number,
                                        amount: row.amount_with_tax,
                                    })}
                                    customButton={
                                        <TooltipButton title={t('validate_payment')}>
                                            <Button
                                                size="small"
                                                variant="contained"
                                                color="primary"
                                                aria-label="action"
                                                style={{ marginRight: 8 }}
                                                className={globalClasses.btnAutoWidth}
                                            >
                                                <CheckIcon fontSize="small" />
                                            </Button>
                                        </TooltipButton>
                                    }
                                    onOK={() => this.handleAction(row, { price: row.amount_with_tax })}
                                />
                            </>
                        );
                    }
                    if (
                        row.payment_type === BillPaymentType.ADYEN &&
                        (row.status === BillStatus.PAYMENT_FAIL_HARD || row.status === BillStatus.PAYMENT_FAIL_SOFT)
                    ) {
                        res = this.canRetryPayment && (
                            <TooltipButton title={t('credit.retry')}>
                                <Button
                                    size="small"
                                    variant="contained"
                                    color="primary"
                                    style={{ marginRight: 8 }}
                                    onClick={this.handleRetryPaiement(row.id)}
                                    className={globalClasses.btnAutoWidth}
                                >
                                    <Icon fontSize="small">cached</Icon>
                                </Button>
                            </TooltipButton>
                        );
                    }

                    return (
                        <>
                            {res}
                            {this.canWriteCreditNote && (
                                <TooltipButton title={t('credit.add')}>
                                    <Button
                                        size="small"
                                        variant="contained"
                                        color="primary"
                                        style={{ marginRight: 8 }}
                                        component={RouterLink}
                                        to={`${paths.billsCreditNotesAdd}?bill_number=${row.bill_number}&id=${row.id}&type=${CreditNoteType.MONTH}`}
                                        className={globalClasses.btnAutoWidth}
                                    >
                                        <Icon fontSize="small">add</Icon>
                                    </Button>
                                </TooltipButton>
                            )}
                            {this.canRectify && row.status !== BillStatus.PAID && (
                                <TooltipButton title={t('credit.modify')}>
                                    <Button
                                        size="small"
                                        variant="contained"
                                        color="primary"
                                        style={{ marginRight: 8 }}
                                        href={`${paths.billsRectificationsAdd}?bill_number=${row.bill_number}&id=${row.id}`}
                                        className={globalClasses.btnAutoWidth}
                                    >
                                        <Icon fontSize="small">receipt</Icon>
                                    </Button>
                                </TooltipButton>
                            )}
                        </>
                    );
                },
            });
        }

        return headers;
    };

    renderActions = (): ActionCustom[] => {
        const { t } = this.props;

        const actions: ActionCustom[] = [];

        if (this.canExport) {
            const prevMonth = DateUtils.getPreviousMonth();
            const defaultMonth = DateUtils.dateFormat(prevMonth.getTime(), 'yyyy-mm');

            actions.push({
                label: t('grouped.export.title'),
                customView: (
                    <Dialog
                        btnTitle={t('grouped.export.title')}
                        description={t('grouped.export.description')}
                        customButton={<div>{t('grouped.export.title')}</div>}
                        formComponent={
                            <>
                                <FormControl key={'formStartDate'} style={{ display: 'flex' }}>
                                    <div>{t('grouped.export.start_date')}</div>
                                    <Input
                                        type="month"
                                        name={'start_date'}
                                        required={true}
                                        defaultValue={defaultMonth}
                                    />
                                    {/* <br /> */}
                                    {/* <div>{t('booking:refound.amount_hint')}</div> */}
                                </FormControl>
                            </>
                        }
                        onOK={(fields) => this.handleExport(fields.start_date, true)}
                    />
                ),
            });
            actions.push({
                label: t('grouped.export.title_sepa'),
                customView: (
                    <Dialog
                        btnTitle={t('grouped.export.title_sepa')}
                        description={t('grouped.export.description')}
                        customButton={<div>{t('grouped.export.title_sepa')}</div>}
                        formComponent={
                            <>
                                <FormControl key={'formStartDate'} style={{ display: 'flex' }}>
                                    <div>{t('grouped.export.start_date')}</div>
                                    <Input
                                        type="month"
                                        name={'start_date'}
                                        required={true}
                                        defaultValue={defaultMonth}
                                    />
                                </FormControl>
                            </>
                        }
                        onOK={(fields) => this.handleExport(fields.start_date, false)}
                    />
                ),
            });
        }
        return actions;
    };

    /**
     * Handle the export considering the date given and the mode of export
     * @param startDate Start of the period -> end is auto calculated 1 month later
     * @param modeAccountance if true export for accounting, false for sepa
     */
    handleExport = (startDate: Date, modeAccounting: boolean) => {
        if (startDate) {
            const start_date = new Date(startDate);
            start_date.setDate(1);
            start_date.setHours(0);
            const payload = {
                start_date: DateUtils.dateFormat(start_date.getTime(), 'isoDateTime'),
                end_date: DateUtils.dateFormat(DateUtils.getNextMonth(start_date).getTime(), 'isoDateTime'),
            };
            if (modeAccounting) {
                this.props.exportPaiement(payload);
            } else {
                this.props.exportSepa(payload);
            }
        }
    };

    render() {
        const { t } = this.props;
        return (
            <App
                needRights={{ name: AccountRight.RIDER_BILLING_MGMT }}
                disableBreadcrumb
                header={{
                    title: t('title_grouped'),
                    actions: this.renderActions(),
                }}
            >
                <ErrorMessage payload={this.props.groupedBillsActionsError} />
                <EnhancedTable
                    onRef={(ref) => (this.tableRef = ref)}
                    headers={this.headerBuilder()}
                    type={tableRequestPoints.BILLS_GROUPED_BETA}
                    filterParams={this.filterParams}
                />
            </App>
        );
    }
}

const GroupedBillListPageWithStyleAndTranslation = withGlobalStyles(
    withTranslation(['bill', 'common'])(GroupedBillListPage),
);

const mapStateToProps = (state) => ({
    loadingRetryPaiement: pendingSelectors.loadingRetryPaiement(state),
    groupedBillsActionsError: errorSelectors.groupedBillsActionsError(state),
});

const mapDispatchToProps = {
    setPaiement: billActions.setPaiement,
    exportSepa: billActions.exportSepa,
    exportPaiement: billActions.exportPaiement,
    retryPaiement: billActions.retryPaiement,
};

export default connect(mapStateToProps, mapDispatchToProps)(GroupedBillListPageWithStyleAndTranslation);
